/*
 * Shared predicates for the two kinds of `exercise-section` document (see
 * msb-api's lib/schemas/exercise-section.js and
 * lib/api_helpers/common/exercise-section-helpers.js, the server-side
 * mirror of this file):
 *
 *  - "normal"       — the text separator that divides a day's exercises into
 *                     groups. Participates in the shared exercise/section
 *                     `order` number-space.
 *  - "workout-note" — a single free-text note pinned to the top of a workout
 *                     day/session. It is NEVER part of that order space: it
 *                     carries the WORKOUT_NOTE_ORDER sentinel and every
 *                     reader (js/utils/prefix-calculator.js) filters it out
 *                     by `type`.
 *
 * `type` is absent on every document written before the note feature
 * shipped, so a missing `type` means "normal" everywhere. That is the
 * contract the rest of the codebase relies on; don't replace it with an
 * equality check.
 *
 * Kept separate from prefix-calculator.js so loaders, controllers and
 * actions can use these predicates without pulling in the ordering layer.
 */

export const SECTION_TYPES = {
  NORMAL: "normal",
  WORKOUT_NOTE: "workout-note",
};

/*
 * Mirrors msb-api's WORKOUT_NOTE_ORDER sentinel. Readers here never trust
 * `order` for a note (they filter by `type`), so this constant only matters
 * if a raw section list is sorted before being filtered.
 */
export const WORKOUT_NOTE_ORDER = -1000;

/** Keep in sync with MAX_WORKOUT_NOTE_LENGTH in msb-api exercise-section-helpers.js */
export const MAX_WORKOUT_NOTE_LENGTH = 2000;

export const isWorkoutNoteSection = (section) =>
  section?.type === SECTION_TYPES.WORKOUT_NOTE;

/** A missing `type` is a pre-feature document, i.e. a normal separator. */
export const isNormalSection = (section) =>
  !!section && !isWorkoutNoteSection(section);

export const excludeWorkoutNotes = (sections = []) =>
  sections.filter(isNormalSection);

export const onlyWorkoutNotes = (sections = []) =>
  sections.filter(isWorkoutNoteSection);

/*
 * At most one note per scope, but the copy/paste/push/send flows on the API
 * side can insert into a destination without clearing it first, so a
 * duplicate is reachable. Newest wins, which makes a duplicate invisible
 * here rather than a rendering bug (two blocks, or a random one shown).
 */
export const pickWorkoutNote = (sections = []) =>
  onlyWorkoutNotes(sections).reduce(
    (best, section) =>
      !best || (section.createdDate ?? 0) > (best.createdDate ?? 0)
        ? section
        : best,
    null
  );

export const splitSections = (sections = []) => ({
  sections: excludeWorkoutNotes(sections),
  note: pickWorkoutNote(sections),
});
